import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const LocationSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [mapActive, setMapActive] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const openInMaps = () => {
    window.open('https://maps.google.com/?q=El+Prat+Nou,+08519+Malla,+Barcelona', '_blank');
  };

  return (
    <section 
      ref={ref}
      className="py-16 md:py-20 px-6 bg-gradient-to-b from-[#F9F7F2] to-[#F5F0EB]"
      data-testid="location-section"
    >
      <motion.div
        className="max-w-4xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Title */}
        <motion.div className="text-center mb-10 md:mb-12" variants={itemVariants}>
          <span className="font-accent text-2xl md:text-3xl text-[#CCA598]">¿Cómo llegar?</span>
          <h2 className="font-heading text-3xl md:text-4xl text-[#5D4037] mt-1">
            Ubicación
          </h2>
          <p className="font-body text-base text-[#8D6E63] mt-3 max-w-md mx-auto">
            Aquí tenéis las indicaciones para llegar sin problema.
          </p>
        </motion.div>

        {/* Venue Info */}
        <motion.div 
          className="text-center mb-6"
          variants={itemVariants}
        >
          <div className="inline-flex items-center gap-2 mb-2 bg-[#CCA598]/10 px-4 py-2 rounded-full">
            <MapPin className="w-4 h-4 text-[#8D6E63]" />
            <h3 className="font-heading text-lg md:text-xl text-[#5D4037]">El Prat Verd</h3>
          </div>
          <p className="font-body text-sm text-[#8D6E63]">
            El Prat Nou, s/n, 08519 Malla, Barcelona
          </p>
        </motion.div>

        {/* Venue Photos */}
        <motion.div 
          className="mb-8 mx-auto max-w-4xl"
          variants={itemVariants}
        >
          <div className="overflow-hidden shadow-lg border-4 border-white/80">
            <img 
              src="https://customer-assets.emergentagent.com/job_nuestra-boda/artifacts/gipp3msh_image.png"
              alt="El Prat Verd - Fotos del lugar"
              className="w-full h-auto"
              data-testid="venue-photos"
            />
          </div>
        </motion.div>

        {/* Map Container */}
        <motion.div 
          className={`map-container aspect-video w-full max-w-3xl mx-auto ${mapActive ? 'active' : ''}`}
          variants={itemVariants}
          onClick={() => setMapActive(true)}
          data-testid="map-container"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2978.6547321023456!2d2.2456789!3d41.9567890!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4d7c8b9a0e1f3%3A0x1234567890abcdef!2sEl%20Prat%20Nou%2C%2008519%20Malla%2C%20Barcelona!5e0!3m2!1ses!2ses!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ubicación de la boda"
          />
        </motion.div>

        {/* Open in Maps Button */}
        <motion.div 
          className="text-center mt-6"
          variants={itemVariants}
        >
          <Button
            onClick={openInMaps}
            variant="outline"
            className="font-body text-sm tracking-wider uppercase border-[#CCA598] text-[#5D4037] hover:bg-[#CCA598]/10 hover:border-[#8D6E63] transition-all duration-300 px-6 py-3 rounded-none"
            data-testid="open-maps-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Abrir en Google Maps
          </Button>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default LocationSection;
